/**
 * Model category taxonomy — finer tags for year themes + debug filters.
 */

/** @type {{ id: string, label: string, group: string }[]} */
export const CATEGORY_META = [
  { id: 'animals', label: 'Animals', group: 'creatures' },
  { id: 'superheroes', label: 'Superheroes', group: 'people' },
  { id: 'screen_icons', label: 'Screen icons', group: 'people' },
  { id: 'atelier_folk', label: 'Atelier folk', group: 'people' },
  { id: 'furniture', label: 'Furniture', group: 'things' },
  { id: 'workshop', label: 'Workshop & gear', group: 'things' },
  { id: 'props', label: 'Props & decor', group: 'things' },
  { id: 'resources', label: 'Resources', group: 'things' },
  { id: 'transport', label: 'Transport', group: 'things' },
  { id: 'flora', label: 'Flora', group: 'wilds' },
  { id: 'terrain', label: 'Terrain', group: 'wilds' },
  { id: 'buildings', label: 'Buildings', group: 'places' },
  { id: 'dungeons', label: 'Dungeons & graves', group: 'places' },
  { id: 'tutorial', label: 'Tutorial', group: 'meta' },
];

export const CATEGORY_IDS = CATEGORY_META.map((c) => c.id);

const SUPERHERO_RE =
  /\b(batman|batgirl|batmanbeyond|superman|bizarrosuperman|supergirl|spiderman|spidey|italianspiderman|wolverine|deadpool|ironman|capamerica|hulk|thor|flash|flashoriginal|greenlantern|greenarrow|wonderwoman|aquaman|catwoman|harleyquinn|joker|jokerkillingjoke|riddler|penguin|bane|poisonivy|mrfreeze|nightwing|robin|hawkgirl|hawkman|daredevil|punisher|venom|carnage|greengoblin|mysterio|kingpin|vulture|gambit|cyclops|colossus|storm|rogue|magneto|mystique|nightcrawler|jubilee|jeanphoenix|beast|xavier|silversurfer|humantorch|invisiblewoman|mrfantastic|thething|ghostrider|spawn|plasticman|spaceghost|birdman|phantom|mandrake)\b/i;

const SCREEN_RE =
  /\b(neo|trinity|morpheus|agentsmith|rickdeckard|roybatty|leeloo|picard|morty|rick|picklerick|finn|jake|bmo|marceline|princessbubblegum|walterwhite|jessiepinkman|saulgoodman|gustavofring|mikeehrmantraut|mulder|scully|skinner|smokingman|frohike|langley|byers|vincentvega|miawallace|juleswinfield|marsellus|butch|mrblonde|mrwhite|mrorange|mrpink|mrbrown|mrblue|aceventura|austinpowers|rocky|ivandrago|brucelee|freeman|beatrixkiddo|zorro|loneranger|shaolin|matrix|deckard|resident|devo|mrt|frankdrebbin|korben|korbendallas|nacho|machunga|spiderjerusalem|maxx|malefico|duende)\b/i;

const ANIMAL_RE =
  /\b(cat|fox|dog|wolf|deer|horse|dragon|rex|trex|bird|fish|bear|pig|cow|sheep|chicken|snake|frog|rabbit|crow|owl|boar|crab|shark|whale|duck|eagle|lion|tiger|rat|mouse|bat|bee|bug|insect|spider|demon dog|npc chicken|npc demon)\b/i;

/** Year themes keyed to the finer taxonomy (calendar month fallbacks).
 *  Authoritative Season One ranges live in scripts/build-year-plan.mjs → year-calendar.json.
 */
export const YEAR_THEME_DEFS = [
  { id: 'harvest', label: 'Harvest Fair', months: [9], categories: ['flora', 'furniture', 'props', 'resources', 'atelier_folk'] },
  { id: 'legends', label: 'Autumn Legends', months: [10], categories: ['screen_icons', 'superheroes', 'atelier_folk', 'props'] },
  { id: 'undercroft', label: 'Undercroft', months: [11], categories: ['dungeons', 'props', 'workshop', 'resources'] },
  { id: 'frost_halls', label: 'Frost Halls', months: [11, 12], categories: ['buildings', 'terrain', 'furniture', 'transport', 'props'] },
  { id: 'new_year', label: 'New Year Frost', months: [12, 1], categories: ['terrain', 'flora', 'buildings', 'props', 'furniture'] },
  { id: 'winter_wildlife', label: 'Winter Wildlife', months: [1, 2], categories: ['animals', 'flora', 'terrain', 'props', 'dungeons'] },
  { id: 'screen_week', label: 'Silver Screen', months: [2, 3], categories: ['screen_icons', 'props', 'furniture'] },
  { id: 'workshop', label: 'Workshop Relics', months: [3], categories: ['workshop', 'furniture', 'resources', 'props', 'atelier_folk'] },
  { id: 'spring_fauna', label: 'Spring Fauna', months: [4], categories: ['animals', 'flora', 'terrain', 'props'] },
  { id: 'heroes', label: 'Cape & Cowl', months: [4, 5], categories: ['superheroes', 'workshop', 'props'] },
  { id: 'gardens', label: 'Gardens & Wilds', months: [5, 6], categories: ['flora', 'terrain', 'animals', 'furniture'] },
  { id: 'folk_summer', label: 'Atelier Folk', months: [6, 7], categories: ['atelier_folk', 'props', 'furniture', 'buildings'] },
  { id: 'keeps', label: 'Keeps & Towers', months: [7, 8], categories: ['buildings', 'dungeons', 'props', 'workshop'] },
  { id: 'late_summer', label: 'Late Summer Parade', months: [8, 9], categories: ['screen_icons', 'superheroes', 'animals', 'flora', 'transport'] },
];

/**
 * Pretty display name from raw MagicaVoxel / file name.
 */
export function displayNameFromRaw(raw) {
  let s = String(raw || 'Model').trim();
  s = s.replace(/^chr[_\s-]+/i, '');
  s = s.replace(/^npc[_\s-]+/i, '');
  s = s.replace(/^decoration[_\s-]+/i, '');
  s = s.replace(/^vegetation[_\s-]+/i, '');
  s = s.replace(/[_-]+/g, ' ');
  s = s.replace(/\s+/g, ' ').trim();
  // Camel / glued tokens: Capamerica → Cap America (light touch)
  s = s.replace(/([a-z])([A-Z])/g, '$1 $2');
  s = s.replace(/\b\w/g, (c) => c.toUpperCase());
  // Known glued fixes
  const fixes = {
    Capamerica: 'Cap America',
    Ironman: 'Iron Man',
    Wonderwoman: 'Wonder Woman',
    Greenlantern: 'Green Lantern',
    Greenarrow: 'Green Arrow',
    Greengoblin: 'Green Goblin',
    Deadpool: 'Deadpool',
    Spidey: 'Spidey',
    Italianspiderman: 'Italian Spiderman',
    Batmanbeyond: 'Batman Beyond',
    Bizarrosuperman: 'Bizarro Superman',
    Flashoriginal: 'Flash Original',
    Harleyquinn: 'Harley Quinn',
    Humantorch: 'Human Torch',
    Invisiblewoman: 'Invisible Woman',
    Mrfantastic: 'Mr Fantastic',
    Thething: 'The Thing',
    Ghostrider: 'Ghost Rider',
    Nightcrawler: 'Nightcrawler',
    Jeanphoenix: 'Jean Phoenix',
    Silversurfer: 'Silver Surfer',
    Poisonivy: 'Poison Ivy',
    Mrfreeze: 'Mr Freeze',
    Jokerkillingjoke: 'Joker Killing Joke',
    Princessbubblegum: 'Princess Bubblegum',
    Picklerick: 'Pickle Rick',
    Walterwhite: 'Walter White',
    Jessiepinkman: 'Jessie Pinkman',
    Saulgoodman: 'Saul Goodman',
    Gustavofring: 'Gustavo Fring',
    Mikeehrmantraut: 'Mike Ehrmantraut',
    Vincentvega: 'Vincent Vega',
    Miawallace: 'Mia Wallace',
    Juleswinfield: 'Jules Winfield',
    Aceventura: 'Ace Ventura',
    Austinpowers: 'Austin Powers',
    Brucelee: 'Bruce Lee',
    Ivandrago: 'Ivan Drago',
    Rickdeckard: 'Rick Deckard',
    Roybatty: 'Roy Batty',
    Agentsmith: 'Agent Smith',
    Korbendallas: 'Korben Dallas',
    Loneranger: 'Lone Ranger',
    Beatrixkiddo: 'Beatrix Kiddo',
    Spiderjerusalem: 'Spider Jerusalem',
    'T Rex': 'T-Rex',
    Trex: 'T-Rex',
  };
  if (fixes[s]) return fixes[s];
  // Keep identity numbers (Monument 7); strip only explicit VariantN suffixes
  return s.replace(/\s+Variant\s*\d+$/i, '').trim() || 'Model';
}

function norm(s) {
  return String(s || '')
    .toLowerCase()
    .replace(/^chr[_\s-]+/, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/**
 * Classify into fine category.
 * @param {{ name?: string, id?: string, pack?: string, category?: string }} entry
 */
export function classifyModel(entry) {
  const id = String(entry.id || '');
  const pack = String(entry.pack || '');
  const raw = entry.name || id;
  const n = norm(raw);
  const nId = norm(id);

  if (entry.category === 'tutorial' || id.includes('tutorial') || pack === 'tutorial') {
    return 'tutorial';
  }

  if (ANIMAL_RE.test(n) || ANIMAL_RE.test(nId) || /\bnpc (demon )?dog\b/.test(n)) {
    return 'animals';
  }

  // VoxFriends / pop culture people
  if (pack === 'voxfriends' || id.startsWith('vf_')) {
    if (SUPERHERO_RE.test(n) || SUPERHERO_RE.test(nId)) return 'superheroes';
    if (SCREEN_RE.test(n) || SCREEN_RE.test(nId)) return 'screen_icons';
    return 'screen_icons'; // default pop-culture cast
  }

  // Ephtracy curated people
  if (pack === 'ephtracy' || id.includes('_ephtracy')) {
    if (ANIMAL_RE.test(n)) return 'animals';
    if (/\b(monument|nature|snow|teapot|sword|bow)\b/.test(n)) {
      if (/\b(sword|bow|teapot)\b/.test(n)) return /\bteapot\b/.test(n) ? 'props' : 'workshop';
      if (/\b(nature|snow)\b/.test(n)) return 'flora';
      if (/\bmonument\b/.test(n)) return 'buildings';
    }
    if (/\b(knight|samurai|elder|mom|man|gumi|rain|sasami|tama|tsurugi|tale|poem|sol)\b/.test(n)) {
      return 'atelier_folk';
    }
  }

  // Medieval pack by name prefixes
  if (/\b(vegetation|tree|grass|crop|mushroom|palm|wheat|fire log)\b/.test(n)) return 'flora';
  if (/\b(desert stone|dirt chunk|obelisk|rough stone|resource stone)\b/.test(n)) return 'terrain';
  if (/\b(desert urn|desert water|desert tablet)\b/.test(n)) return 'props';
  if (/\b(dungeon|goblin|graveyard|coffin|tomb|grave stone)\b/.test(n)) return 'dungeons';
  if (/\b(shop|building|shelter|wall|roof|well|monument|swamp building)\b/.test(n)) return 'buildings';
  if (/\b(table|chair|stool)\b/.test(n)) return 'furniture';
  if (/\b(crafting|armor|weapon|sword|bow|sawmill|fire pit)\b/.test(n)) return 'workshop';
  if (/\b(train|track|cart|wagon|transport)\b/.test(n)) return 'transport';
  if (/\b(resource|ore|ingot)\b/.test(n)) return 'resources';
  if (/\b(npc|player torch|light player)\b/.test(n)) return 'atelier_folk';
  if (/\b(decoration|chest|barrel|crate|cauldron|candle|torch|potion|bookshelf|banner|urn|mug|cup|bowl|plate)\b/.test(n)) {
    return 'props';
  }

  // Fallbacks from legacy coarse tags
  if (entry.category === 'animals') return 'animals';
  if (entry.category === 'nature') return 'flora';
  if (entry.category === 'architecture') return 'buildings';
  if (entry.category === 'characters') return 'atelier_folk';
  if (entry.category === 'objects') return 'props';

  return 'props';
}

export function guessAnswersFor(displayName) {
  const low = displayName.toLowerCase();
  const compact = low.replace(/\s+/g, '');
  const out = new Set([low, displayName, compact]);
  // drop leading decoration noise already cleaned
  return Array.from(out);
}
